/**
 * Listening.jsx
 * Monitors, what they counted, and what changed sharply.
 *
 * **The screen argues with its own name, on purpose.** Proper social listening
 * searches public posts by keyword across many networks. Meta offers no such
 * endpoint — Facebook's public post search was withdrawn, Instagram never had
 * one — so this watches the three things Kreeboom can genuinely reach, and
 * says which is which on every monitor rather than letting the word do work
 * the data cannot.
 *
 * Nothing here spends AI credit. The only cost is Meta's Instagram hashtag
 * ration, shared with the Hashtags screen and shown alongside.
 *
 * Dependencies: services/api.js, components/listening/*, hooks/useBrands.jsx
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { Bell, Chart, Check, Close, Eye, Hash, Plus, Refresh, Users, Warning } from '../components/Icons.jsx';
import NoBrands from '../components/NoBrands.jsx';
import VolumeChart, { SLOTS } from '../components/listening/VolumeChart.jsx';
import { when } from '../components/inbox/shared.js';

const KINDS = [
  { key: 'keyword', label: 'Keyword', Icon: Eye, placeholder: 'Hootsuite OR Buffer OR Metricool' },
  { key: 'hashtag', label: 'Hashtag', Icon: Hash, placeholder: 'socialmediascheduler' },
  { key: 'competitor', label: 'Competitor', Icon: Users, placeholder: 'hootsuite' },
];

const SEVERITY = { high: 'danger', medium: 'warn', low: 'muted' };

export default function Listening() {
  const navigate = useNavigate();
  const { brands, loading: brandsLoading } = useBrands();
  const [brandId] = useSelectedBrand();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ kind: 'keyword', query: '', name: '' });

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      setData(await api.listening.get({ brandId, days: 30 }));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add(e) {
    e.preventDefault();
    const query = draft.query.trim();
    if (!query) return;

    setBusy(true);
    setError(null);
    try {
      await api.listening.create({ brandId, ...draft, query });
      setDraft({ kind: draft.kind, query: '', name: '' });
      setAdding(false);
      setNote('Monitor added. It will take its first reading tonight, or press Check now.');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function runAll() {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      const res = await api.listening.runAll(brandId);
      setNote(res.message);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function act(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!brandsLoading && brands.length === 0) return <NoBrands />;

  const monitors = data?.monitors ?? [];
  const alerts = data?.alerts ?? [];
  const unseen = alerts.filter((a) => !a.seen_at).length;
  const totalVolume = (data?.readings ?? []).reduce((sum, r) => sum + (r.volume ?? 0), 0);
  const quota = data?.hashtagQuota;

  return (
    <>
      <div className="page-head page-head--hero">
        <div>
          <div className="page-head__eyebrow">Analyse &amp; grow</div>
          <h1>
            <span className="copilot-mark">
              <Chart size={26} />
            </span>
            Listening <span className="badge badge--accent">Beta</span>
          </h1>
          <p>
            Watch hashtags, competitors and keywords, and get told when something changes sharply.
          </p>
        </div>

        <div className="page-head__actions">
          <button className="btn" onClick={runAll} disabled={busy || monitors.length === 0}>
            <Refresh size={16} /> {busy ? 'Checking…' : 'Check now'}
          </button>
          <button className="btn btn--primary" onClick={() => setAdding((v) => !v)}>
            <Plus size={16} /> New monitor
          </button>
        </div>
      </div>

      {/* Said before anything else, because the name promises the open web and
          this cannot reach it. */}
      <div className="notice notice--info">
        <Warning />
        <span>
          Meta has no public-post keyword search, so this is not web-wide listening. A monitor
          watches an Instagram hashtag, a named competitor account, or words in the comments and
          messages you have already received. Each one says which, below.
        </span>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      <div className="stat-row stat-row--inbox">
        {[
          ['Monitors', `${monitors.length}`, `${monitors.filter((m) => m.enabled).length} running`],
          ['Counted in 30 days', String(totalVolume), 'across every monitor'],
          ['Unread alerts', String(unseen), `${alerts.length} in total`],
          [
            'Hashtag allowance',
            quota ? `${quota.used}/${quota.limit}` : '—',
            quota?.remaining === 0
              ? 'used up — hashtag monitors cannot check until a slot frees'
              : 'distinct tags in 7 days, shared with the Hashtags screen',
          ],
        ].map(([label, value, sub]) => (
          <div className="stat stat--flat" key={label}>
            <span className="stat__label">{label}</span>
            <span className="stat__value">{value}</span>
            <span className="stat__note">{sub}</span>
          </div>
        ))}
      </div>

      {adding && (
        <form className="card monitor-form" onSubmit={add}>
          <div className="card__head">
            <span className="tile">
              <Plus size={21} />
            </span>
            <div className="card__head-text">
              <h2>New monitor</h2>
              <p>Pick what kind of thing to watch. Each sees something different.</p>
            </div>
          </div>

          <div className="kind-picker">
            {KINDS.map((k) => (
              <button
                type="button"
                key={k.key}
                className={`platform-pick${draft.kind === k.key ? ' is-active' : ''}`}
                onClick={() => setDraft({ ...draft, kind: k.key })}
              >
                <k.Icon size={16} /> {k.label}
              </button>
            ))}
          </div>

          {/* The honest limits of the chosen kind, before it is created. */}
          {data?.coverage?.[draft.kind] && (
            <div className="coverage">
              <p>
                <strong>Sees:</strong> {data.coverage[draft.kind].sees}
              </p>
              <p className="coverage__cannot">
                <Warning size={13} /> {data.coverage[draft.kind].cannot}
              </p>
              <p className="row__sub">{data.coverage[draft.kind].costs}</p>
            </div>
          )}

          <div className="field-row">
            <div className="field">
              <label htmlFor="query">
                {draft.kind === 'keyword'
                  ? 'Words to watch for'
                  : draft.kind === 'hashtag'
                    ? 'Hashtag'
                    : 'Instagram username'}
              </label>
              <input
                id="query"
                value={draft.query}
                maxLength={300}
                placeholder={KINDS.find((k) => k.key === draft.kind)?.placeholder}
                onChange={(e) => setDraft({ ...draft, query: e.target.value })}
              />
              {draft.kind === 'keyword' && (
                <span className="hint">Separate alternatives with OR. Quote a phrase to match it whole.</span>
              )}
            </div>

            <div className="field">
              <label htmlFor="name">Name (optional)</label>
              <input
                id="name"
                value={draft.name}
                maxLength={80}
                placeholder="What to call it"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
          </div>

          <div className="btn-row">
            <button className="btn btn--primary" type="submit" disabled={busy || !draft.query.trim()}>
              <Plus size={15} /> Add monitor
            </button>
            <button className="btn" type="button" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="listen-grid">
        <VolumeChart monitors={monitors} readings={data?.readings ?? []} days={data?.days ?? 30} />

        <section className="card">
          <div className="card__head">
            <span className={`tile ${unseen ? 'tile--warn' : 'tile--ok'}`}>
              <Bell size={21} />
            </span>
            <div className="card__head-text">
              <h2>Alerts</h2>
              <p>Raised only when a monitor moves well outside its usual range.</p>
            </div>
            {unseen > 0 && (
              <div className="card__head-end">
                <button
                  className="btn btn--sm"
                  onClick={() => act(() => api.listening.seen({ brandId }))}
                  disabled={busy}
                >
                  Mark all read
                </button>
              </div>
            )}
          </div>

          {alerts.length === 0 ? (
            <div className="row">
              <span className="tile tile--sm tile--ok">
                <Check size={17} />
              </span>
              <div>
                <div className="row__label">Nothing unusual</div>
                <div className="row__sub">
                  An alert needs {data?.limits?.minHistory ?? 10} days of readings behind it. Below
                  that, a jump is as likely to be noise as news.
                </div>
              </div>
            </div>
          ) : (
            <ul className="alert-list">
              {alerts.map((a) => (
                <li className={`alert${a.seen_at ? ' is-seen' : ''}`} key={a.id}>
                  <span className={`tag tag--${SEVERITY[a.severity] ?? 'muted'}`}>
                    {a.severity}
                  </span>
                  <div className="alert__text">
                    <div className="alert__title">{a.title}</div>
                    {/* The arithmetic, so the alert can be checked rather than believed. */}
                    <div className="alert__detail">{a.detail}</div>
                    <div className="row__sub">{when(a.created_at)}</div>
                  </div>
                  {!a.seen_at && (
                    <button
                      className="idea__remove"
                      onClick={() => act(() => api.listening.seen({ id: a.id }))}
                      title="Mark read"
                      aria-label="Mark read"
                    >
                      <Check size={15} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Eye size={21} />
          </span>
          <div className="card__head-text">
            <h2>Monitors</h2>
            <p>
              {monitors.length === 0
                ? 'Nothing being watched yet.'
                : `${monitors.length} of ${data?.limits?.maxMonitors ?? 24}.`}
            </p>
          </div>
        </div>

        {monitors.length === 0 ? (
          <div className="empty empty--inline">
            <div className="empty__art">
              <Eye size={26} />
            </div>
            <h3>No monitors yet</h3>
            <p>
              Watch a competitor's Instagram account, a hashtag, or words that appear in messages
              people send you.
            </p>
            <button className="btn btn--primary" onClick={() => setAdding(true)}>
              <Plus /> New monitor
            </button>
          </div>
        ) : (
          <div className="rows">
            {monitors.map((m, i) => {
              const kind = KINDS.find((k) => k.key === m.kind);
              const sot = (data?.shareOfMonitored ?? []).find((s) => s.id === m.id);

              return (
                <div className={`row row--wrap${m.enabled ? '' : ' is-off'}`} key={m.id}>
                  <span className={`tile tile--sm`}>
                    {kind && <kind.Icon size={17} />}
                  </span>

                  <div style={{ minWidth: 0 }}>
                    <div className="row__label">
                      <span className={`mon-swatch ${i < SLOTS ? `mon-key--${i + 1}` : 'mon-key--other'}`} />
                      {m.name}
                      <span className="tag tag--muted">{kind?.label ?? m.kind}</span>
                    </div>
                    <div className="row__sub">
                      <span className="mono">{m.query}</span>
                      {' · '}
                      {m.readings > 0
                        ? `${m.readings} readings, latest ${m.latest_volume ?? 0}`
                        : 'never checked'}
                      {sot ? ` · ${sot.percent}% of what these monitors counted` : ''}
                    </div>
                    {m.last_error && (
                      <div className="row__sub mon-error">
                        <Warning size={12} /> {m.last_error}
                      </div>
                    )}
                  </div>

                  <div className="row__end">
                    <button
                      className="btn btn--sm"
                      onClick={() => act(() => api.listening.run(m.id))}
                      disabled={busy}
                    >
                      <Refresh size={14} /> Check
                    </button>
                    <button
                      className="btn btn--sm"
                      onClick={() => act(() => api.listening.update(m.id, { enabled: !m.enabled }))}
                      disabled={busy}
                    >
                      {m.enabled ? 'Pause' : 'Resume'}
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      disabled={busy}
                      onClick={() => {
                        const ok = window.confirm(
                          `Delete "${m.name}"?\n\nIts readings and alerts go with it. Nothing outside Kreeboom changes.`,
                        );
                        if (ok) act(() => api.listening.remove(m.id));
                      }}
                    >
                      <Close size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="card__footnote">
          Monitors are checked once a night. "Share of what these monitors counted" is exactly
          that — not share of voice across the web, which nothing here can measure.
        </p>
      </section>

      {loading && !data && (
        <section className="card">
          <p className="row__sub">Loading…</p>
        </section>
      )}
    </>
  );
}
